import { useLocation, useNavigate } from "@solidjs/router";
import type { Scope } from "@storytell/cp-client";
import { dispatchGlobalEvent, subscribeToGlobalEvent } from "@storytell/events";
import { batch, createEffect, on, onCleanup, onMount } from "solid-js";
import { createStore } from "solid-js/store";
import { isServer } from "solid-js/web";
import type { AssetsService } from "../assets";

/** Legacy bug: first paginated page (~50 ids) was persisted as explicit assetIdList. */
const LEGACY_PAGINATION_PAGE_SIZE = 50;

function shouldHealLegacyPartialAssetScope(saved: Scope.Scope, total: number | undefined): boolean {
  if (saved.projectIdList && saved.projectIdList.length > 0) return false;
  const ids = saved.assetIdList ?? [];
  return ids.length === LEGACY_PAGINATION_PAGE_SIZE && total !== undefined && total > 0 && total > ids.length;
}

/** Persisted scope with no projectIdList and no concrete knowledge ids (intentional empty or legacy omit). */
function isPersistedEmptyKnowledgeScope(scope: Scope.Scope): boolean {
  if ((scope.projectIdList?.length ?? 0) > 0) return false;
  return (
    (scope.assetIdList?.length ?? 0) === 0 &&
    (scope.conceptIdList?.length ?? 0) === 0 &&
    (scope.labelIdList?.length ?? 0) === 0 &&
    (scope.viewIdList?.length ?? 0) === 0 &&
    (scope.attachmentIdList?.length ?? 0) === 0 &&
    (scope.artifactIdList?.length ?? 0) === 0
  );
}

/** Status for an asset that is being uploaded (temp entry in scope). */
export type UploadingAssetStatus = "uploading" | "processing" | "error";

export const createChatServiceScope = (deps: {
  projectId: () => string | undefined;
  workspaceId: () => string | undefined;
  assets: AssetsService;
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [state, setState] = createStore({
    concepts: [] as string[],
    labels: [] as string[],
    assets: [] as string[],
    views: [] as string[],
    uploadingAssets: [] as { id: string; filename: string; status: UploadingAssetStatus }[],
    // Track if scope was explicitly set (via "Chat with" or manual selection)
    // to avoid auto-selecting all assets
    scopeWasExplicitlySet: false,
    // Asset IDs excluded from implicit full-project scope (user unchecked individual files)
    implicitExcludedAssets: [] as string[],
    lastCheckedUrl: "",
  });

  // Dedupe orphan-validation across the many sites that call loadScopeFromMessage
  // (initial chat fetch, last-message effect on every turn, branch switch, edit
  // start/cancel). The persisted assetIdList rarely changes turn-to-turn, so
  // skip the GET /assets?ids=… round-trip when the same id set was just checked.
  let lastValidatedExplicitIdsKey: string | undefined;

  let lastSeenProjectId: string | undefined = deps.projectId();

  const navigateToNewChat = () => {
    const pathname = location.pathname;
    // User can chat from homepage or any project page - the prompt bar is always available there
    const canChatFromCurrentPage = pathname === "/" || pathname.startsWith("/project/");
    if (!canChatFromCurrentPage) {
      requestAnimationFrame(() => {
        navigate("/");
      });
    }
  };

  /**
   * Use implicit "entire project" file scope (efficient projectIdList on submit).
   * Does not enumerate asset IDs — the asset store may only hold a paginated subset.
   */
  const selectAllAssets = () => {
    const projectId = deps.projectId();
    if (!projectId) return;

    batch(() => {
      setState("scopeWasExplicitlySet", false);
      setState("assets", []);
      setState("implicitExcludedAssets", []);
    });
  };

  /** Exit implicit "entire project library" file scope: explicit empty files, keep labels/concepts/views. */
  const exitImplicitProjectFileScope = () => {
    batch(() => {
      setState("scopeWasExplicitlySet", true);
      setState("assets", []);
    });
  };

  /**
   * Promote implicit library scope to explicit while preserving a specific
   * set of asset IDs (usually the currently-powering file set snapshotted
   * by the caller). Used when a picker action must register an explicit add
   * (e.g. a spreadsheet) but the existing implicit file coverage should be
   * kept intact — addAsset() otherwise silently no-ops in implicit mode.
   */
  // Invariant: only invoked while in implicit mode, so uploadingAssets are never already in state.assets (addAsset no-ops during implicit mode).
  const promoteToExplicit = (preservedAssetIds: string[]) => {
    const deduped = Array.from(new Set(preservedAssetIds));
    batch(() => {
      setState("scopeWasExplicitlySet", true);
      setState("assets", deduped);
      setState("implicitExcludedAssets", []);
    });
  };

  /**
   * Implicit project-wide file scope: exclude a single asset without leaving implicit mode.
   * Tracks the excluded ID so the prompt bar count stays accurate (total - excluded).
   * On submit, excluded IDs are filtered out of the full asset list.
   */
  const implicitLibraryExcludeAsset = (assetId: string) => {
    if (state.implicitExcludedAssets.includes(assetId)) return;
    setState("implicitExcludedAssets", (prev) => [...prev, assetId]);
    dispatchGlobalEvent("removed-item-from-prompt-scope", { id: assetId });
  };

  const implicitLibraryUnexcludeAsset = (assetId: string) => {
    if (!state.implicitExcludedAssets.includes(assetId)) return;
    setState("implicitExcludedAssets", (prev) => prev.filter((id) => id !== assetId));
  };

  // Effect to track URL changes (e.g. new vs existing chat). Implicit full-project scope
  // is represented by scopeWasExplicitlySet === false and empty asset ids — no selectAllAssets().
  createEffect(
    on(
      () => `${location.pathname}${location.search}`,
      (currentUrl) => {
        if (isServer) return;

        if (state.lastCheckedUrl === currentUrl) return;
        setState("lastCheckedUrl", currentUrl);
      }
    )
  );

  onMount(() => {
    const unsubscribeAdded = subscribeToGlobalEvent("added-mention-to-prompt-bar", (event) => {
      batch(() => {
        // Skip project_knowledge mentions - we don't use that concept anymore
        if (event.detail.id === "project_knowledge") {
          return;
        }

        // In implicit library mode the store stays untouched so the picker keeps the whole library;
        // the submit merge narrows only this turn to the mentioned file.
        if (event.detail.id.startsWith("asset_")) {
          if (state.scopeWasExplicitlySet) {
            addAsset(event.detail.id);
          }
        } else if (event.detail.id.startsWith("concept_")) {
          addConcept(event.detail.id);
        } else if (event.detail.id.startsWith("label_")) {
          addLabel(event.detail.id);
        } else if (event.detail.id.startsWith("view_")) {
          addView(event.detail.id);
        }
      });

      navigateToNewChat();
    });

    const unsubscribeRemoved = subscribeToGlobalEvent("removed-mention-from-prompt-bar", (event) => {
      batch(() => {
        if (event.detail.id === "project_knowledge") {
          return;
        }
        if (event.detail.id.startsWith("asset_")) {
          removeAsset(event.detail.id);
        } else if (event.detail.id.startsWith("concept_")) {
          removeConcept(event.detail.id);
        } else if (event.detail.id.startsWith("label_")) {
          removeLabel(event.detail.id);
        } else if (event.detail.id.startsWith("view_")) {
          removeView(event.detail.id);
        }
      });

      navigateToNewChat();
    });

    const unsubscribeAddedToPromptScope = subscribeToGlobalEvent("add-items-to-prompt-scope", (event) => {
      batch(() => {
        // Mark that scope was explicitly set (via "Chat with" button)
        setState("scopeWasExplicitlySet", true);

        setState("assets", []);
        setState("concepts", []);
        setState("labels", []);
        setState("views", []);

        for (const id of event.detail.ids) {
          if (id.startsWith("asset_")) {
            addAsset(id);
          } else if (id.startsWith("concept_")) {
            addConcept(id);
          } else if (id.startsWith("label_")) {
            addLabel(id);
          } else if (id.startsWith("view_")) {
            addView(id);
          }
        }
      });

      navigateToNewChat();
    });

    const unsubscribeUserContextChanged = subscribeToGlobalEvent("user-context-changed", (event) => {
      const newProjectId = event.detail?.projectId;
      const projectChanged = !!newProjectId && newProjectId !== lastSeenProjectId;
      lastSeenProjectId = newProjectId ?? lastSeenProjectId;

      if (event.detail?.preserveChat) {
        if (!projectChanged) return;
        lastValidatedExplicitIdsKey = undefined;
        batch(() => {
          setState("assets", []);
          setState("concepts", []);
          setState("labels", []);
          setState("views", []);
          setState("implicitExcludedAssets", []);
          setState("uploadingAssets", []);
          setState("scopeWasExplicitlySet", false);
        });
        return;
      }
      lastValidatedExplicitIdsKey = undefined;
      clearAll();
    });

    onCleanup(() => {
      unsubscribeAdded();
      unsubscribeRemoved();
      unsubscribeAddedToPromptScope();
      unsubscribeUserContextChanged();
    });
  });

  const addView = (viewId: string) => {
    if (state.views.includes(viewId)) return;
    batch(() => {
      setState("views", (prev) => [...prev, viewId]);
    });
  };

  const removeView = (viewId: string) => {
    if (!state.views.includes(viewId)) return;
    batch(() => {
      setState("views", (prev) => prev.filter((id) => id !== viewId));
    });
  };

  const addLabel = (labelId: string) => {
    if (state.labels.includes(labelId)) return;
    setState("labels", (prev) => [...prev, labelId]);
  };

  const removeLabel = (labelId: string) => {
    if (!state.labels.includes(labelId)) return;
    setState("labels", (prev) => prev.filter((id) => id !== labelId));
  };

  const addAsset = (assetId: string) => {
    if (state.assets.includes(assetId)) return;
    // If using implicit full-project scope, the newly uploaded file is already
    // part of the project and will be included via projectIdList on submit.
    // Don't switch to explicit mode — that would drop all other project files.
    if (!state.scopeWasExplicitlySet) return;
    setState("assets", (prev) => [...prev, assetId]);
  };

  const addUploadingAsset = (filename: string) => {
    const existing = state.uploadingAssets.find((a) => a.filename === filename && a.status === "uploading");
    if (existing) {
      return existing.id;
    }
    const assetId = `temp_${performance.now()}_${Math.random().toString(36).substring(2, 9)}`;
    if (state.uploadingAssets.some((a) => a.id === assetId)) return assetId;
    setState("uploadingAssets", (prev) => [...prev, { id: assetId, filename, status: "uploading" }]);
    addAsset(assetId);
    return assetId;
  };

  const updateUploadingAssetStatus = (assetId: string, status: UploadingAssetStatus) => {
    setState("uploadingAssets", (prev) => prev.map((a) => (a.id === assetId ? { ...a, status } : a)));
  };

  const removeUploadingAsset = (assetId: string) => {
    setState("uploadingAssets", (prev) => prev.filter((a) => a.id !== assetId));
    removeAsset(assetId);
  };

  const removeAsset = (assetId: string) => {
    if (!state.assets.includes(assetId)) return;
    batch(() => {
      setState("assets", (prev) => prev.filter((id) => id !== assetId));
      setState("scopeWasExplicitlySet", true);
    });
    dispatchGlobalEvent("removed-item-from-prompt-scope", { id: assetId });
  };

  const addConcept = (conceptId: string) => {
    if (state.concepts.includes(conceptId)) return;
    setState("concepts", (prev) => [...prev, conceptId]);
  };

  const removeConcept = (conceptId: string) => {
    if (!state.concepts.includes(conceptId)) return;
    setState("concepts", (prev) => prev.filter((id) => id !== conceptId));
    dispatchGlobalEvent("removed-item-from-prompt-scope", { id: conceptId });
  };

  const clearAll = (options?: { fromExplicitUserClear?: boolean }) => {
    const toRemove = [...state.assets, ...state.concepts];
    const explicitUserClear = options?.fromExplicitUserClear === true;
    batch(() => {
      setState({
        assets: [],
        concepts: [],
        labels: [],
        views: [],
        implicitExcludedAssets: [],
        // When user clicks "Remove all" in scope picker, treat as explicit choice so we don't auto-select all
        scopeWasExplicitlySet: explicitUserClear,
        lastCheckedUrl: "",
      });
    });
    for (const id of toRemove) {
      dispatchGlobalEvent("removed-item-from-prompt-scope", { id });
    }
  };

  const assets = () => state.assets;
  const concepts = () => state.concepts;
  const views = () => state.views;
  const labels = () => state.labels;
  const uploadingAssets = () => state.uploadingAssets;

  const getScopeForPromptSubmit = (): Scope.Scope => {
    const base: Scope.Scope = {
      $typeName: "query.v3.Scope",
      viewIdList: [],
      textAttachmentList: [],
      attachmentIdList: [],
      assetIdList: [],
      artifactIdList: [],
      labelIdList: [],
      conceptIdList: [],
      projectIdList: [],
      chatIdList: [],
      sessionIdList: [],
      workspaceId: "",
      organizationId: "",
    };

    // Implicit full-project scope: use projectIdList when no exclusions, else enumerate loaded assets minus excluded.
    const projectId = deps.projectId();
    if (!state.scopeWasExplicitlySet && projectId) {
      if (state.implicitExcludedAssets.length === 0) {
        return {
          ...base,
          assetIdList: [] as string[],
          conceptIdList: state.concepts,
          labelIdList: state.labels,
          viewIdList: state.views,
          projectIdList: [projectId],
        };
      }
      const excluded = new Set(state.implicitExcludedAssets);
      const scopeWirePresenceId =
        deps.workspaceId()?.trim() || deps.projectId()?.trim() || "workspace_explicit_empty_knowledge";
      return {
        ...base,
        assetIdList: deps.assets
          .all()
          .map((a) => a.id)
          .filter((id) => !excluded.has(id)),
        conceptIdList: state.concepts,
        labelIdList: state.labels,
        viewIdList: state.views,
        projectIdList: [] as string[],
        workspaceId: scopeWirePresenceId,
      };
    }

    // Optional prompt.scope is omitted on the wire when the nested Scope message encodes "empty"
    // (all default values). The backend then treats scope as absent and expands to full-project KB.
    // A non-empty workspaceId forces the Scope submessage to serialize so PromptScopeFieldIncluded applies.
    const scopeWirePresenceId =
      deps.workspaceId()?.trim() || deps.projectId()?.trim() || "workspace_explicit_empty_knowledge";
    const explicitScope = {
      ...base,
      assetIdList: state.assets,
      conceptIdList: state.concepts,
      labelIdList: state.labels,
      viewIdList: state.views,
      projectIdList: [] as string[],
      workspaceId: scopeWirePresenceId,
    };
    return explicitScope;
  };

  const loadScopeFromMessage = (scope: Scope.Scope, messageProjectId?: string) => {
    const currentProjectId = deps.projectId();
    const foreignProject = !!messageProjectId && !!currentProjectId && messageProjectId !== currentProjectId;
    batch(() => {
      if (scope.projectIdList && scope.projectIdList.length > 0) {
        // Full-project knowledge: keep scopeWasExplicitlySet false so UI uses backend total (not cached asset count).
        setState("scopeWasExplicitlySet", false);
        setState("assets", []);
        setState("concepts", foreignProject ? [] : scope.conceptIdList || []);
        setState("labels", foreignProject ? [] : scope.labelIdList || []);
        setState("views", foreignProject ? [] : scope.viewIdList || []);
      } else {
        setState("concepts", foreignProject ? [] : scope.conceptIdList || []);
        setState("labels", foreignProject ? [] : scope.labelIdList || []);
        setState("views", foreignProject ? [] : scope.viewIdList || []);
        const ids = foreignProject ? [] : scope.assetIdList || [];
        const totalNow = deps.assets.getTotalCount();
        /**
         * Legacy: a bug sent the first paginated page (~50 ids) as explicit assetIdList instead of
         * projectIdList. That forces scopeWasExplicitlySet and breaks pill / "not in scope" counts.
         * If the saved list length matches the default page size and the project has more files,
         * treat as full-project implicit scope. (Rare false positive: user intentionally picked
         * exactly 50 of 119 files — they can narrow scope again in the picker.)
         */
        if (shouldHealLegacyPartialAssetScope(scope, totalNow)) {
          setState("scopeWasExplicitlySet", false);
          setState("assets", []);
        } else if (isPersistedEmptyKnowledgeScope(scope)) {
          // Explicit empty: submit with no project files must stay empty on the next turn, not implicit full-project.
          setState("scopeWasExplicitlySet", true);
          setState("assets", []);
        } else if (foreignProject) {
          setState("scopeWasExplicitlySet", true);
          setState("assets", []);
        } else {
          setState("scopeWasExplicitlySet", true);
          setState("assets", ids);
        }
      }
    });
    if (foreignProject) return;
    if (!isServer) {
      const savedScope = scope;
      const runLegacyHeal = (total: number | undefined) => {
        if (!shouldHealLegacyPartialAssetScope(savedScope, total)) return;
        batch(() => {
          setState("scopeWasExplicitlySet", false);
          setState("assets", []);
        });
      };
      queueMicrotask(() => {
        runLegacyHeal(deps.assets.getTotalCount());
      });
      // Drop orphaned asset IDs from the restored explicit scope. A chat's persisted
      // scope can reference assets that have since been deleted or replaced (e.g. a
      // file re-uploaded with the same name gets a new ID). Those ghost IDs otherwise
      // travel back to the backend on the next prompt and desync the scope picker UI
      // from what the user thinks is selected.
      const dropOrphans = async (): Promise<void> => {
        const total = await deps.assets.fetchTotalCount();
        runLegacyHeal(total);
        if (shouldHealLegacyPartialAssetScope(savedScope, total)) return;
        const explicitIds = savedScope.assetIdList ?? [];
        if (explicitIds.length === 0) return;
        const key = explicitIds.toSorted().join(",");
        if (key === lastValidatedExplicitIdsKey) return;
        lastValidatedExplicitIdsKey = key;
        const validIds = await deps.assets.validateAssetIds(explicitIds);
        if (!validIds) {
          // Network failure: clear key so the next call retries instead of being deduped against a no-op result.
          if (lastValidatedExplicitIdsKey === key) lastValidatedExplicitIdsKey = undefined;
          return;
        }
        const orphans = new Set(explicitIds.filter((id) => !validIds.has(id)));
        if (orphans.size === 0) return;
        setState("assets", (prev) => prev.filter((id) => !orphans.has(id)));
      };
      void dropOrphans();
    }
  };

  return {
    assets,
    addLabel,
    removeLabel,
    views,
    addView,
    removeView,
    addUploadingAsset,
    concepts,
    labels,
    addAsset,
    removeAsset,
    clearAll,
    addConcept,
    removeConcept,
    getScopeForPromptSubmit,
    loadScopeFromMessage,
    uploadingAssets,
    removeUploadingAsset,
    updateUploadingAssetStatus,
    selectAllAssets,
    exitImplicitProjectFileScope,
    promoteToExplicit,
    implicitLibraryExcludeAsset,
    implicitLibraryUnexcludeAsset,
    /** True when draft scope should show full-project file count (new chat default, or message used projectIdList), not length of cached asset IDs. */
    isUsingAllAssets: () => !state.scopeWasExplicitlySet,
    implicitExcludedAssets: () => state.implicitExcludedAssets,
  };
};
